import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

function Pricing() {
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in and premium status
  React.useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setIsLoggedIn(true);
        
        // Check premium status
        const { fetchUserAttributes } = await import('aws-amplify/auth');
        const attributes = await fetchUserAttributes();
        const premiumVal = attributes['custom:premium_status'] || 'false';
        const premiumStatus = premiumVal === 'true' || premiumVal === 'essential';
        setIsPremium(premiumStatus);
        if (premiumVal === 'essential') setUserTier('essential');
        else if (premiumVal === 'true' || premiumVal === 'premium') setUserTier('premium');
        else setUserTier('free');
        console.log('Premium status check:', { attributes, premiumStatus, tier: premiumVal });
      }
    } catch (err) {
      console.error('Error checking user status:', err);
    }
  };

  const handleCheckout = async (tier) => {
    // Check if user is logged in
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // Check if already premium
    if (userTier === 'premium') {
      alert('🎉 Thank you for being a Premium member!\n\nYou already have complete access to all content.\n\nEnjoy your premium features!');
      return;
    }
    
    // Essential user trying to buy essential again
    if (userTier === 'essential' && tier === 'essential') {
      alert('⭐ You already have Essential access!\n\nUpgrade to Premium for Writing Studio and all topic tests.');
      return;
    }

    try {
      setCheckoutLoading(true);
      
      console.log('Starting checkout process for tier:', tier);
      
      // Call Lambda to create Stripe checkout session
      const response = await apiClient.post('/create-checkout-session', { tier });
      
      console.log('Checkout response:', response);
      
      if (response.data && response.data.url) {
        console.log('Redirecting to Stripe:', response.data.url);
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        console.error('No checkout URL in response:', response.data);
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Checkout error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        error: error
      });
      
      // More specific error messages
      let errorMessage = 'Failed to start checkout. ';
      
      if (error.response?.status === 401) {
        errorMessage = 'Please login again to continue with checkout.';
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to access checkout. Please contact support.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again in a few moments or contact support.';
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Network connection issue. Please check your internet connection and try again.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage += 'Please try again or contact support at prepsmart.help@gmail.com';
      }
      
      alert(errorMessage);
      setCheckoutLoading(false);
    }
  };

  const plans = [
    {
      name: 'Free Plan',
      price: '$0',
      period: 'Until Exam Day 2027',
      emoji: '🎓',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: [
        '✅ 12 Full-Length Practice Tests (Selective)',
        '✅ 3 OC Practice Tests (Reading, Maths, Thinking Skills)',
        '✅ All Subjects Covered',
        '✅ 4 Topic-Based Trial Tests',
        '✅ Unlimited Test Attempts',
        '✅ Instant Expert Feedback & Hints',
        '✅ Instant Scoring & Feedback',
        '✅ Timer & Pacing Analysis',
        '✅ Basic Progress Tracking'
      ],
      cta: 'Start Free Practice',
      ctaAction: () => navigate('/exams'),
      popular: false,
      tier: 'free'
    },
    {
      name: 'Essential Bundle',
      price: '$75',
      period: 'Until Exam Day 2027',
      emoji: '⭐',
      gradient: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
      features: [
        '✅ Everything in Free Plan',
        '✅ 40+ Topic-Based Practice Tests',
        '✅ Focused Practice by Topic',
        '✅ Covers Selective & OC Subjects',
        '✅ Exam-Style Interface Matching NSW Format',
        '✅ Step-by-Step Explanations for Every Question',
        '✅ Unlimited Test Attempts & Re-tries',
        '✅ Detailed Performance Analytics',
        '✅ Strengths & Weaknesses Analysis',
        '✅ Regular Content Updates',
        '✅ Email Support'
      ],
      cta: 'Get Essential Access',
      ctaAction: 'checkout-essential',
      popular: false,
      tier: 'essential'
    },
    {
      name: 'Premium Bundle',
      price: '$100',
      period: 'Until Exam Day 2027',
      emoji: '🚀',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      features: [
        '✅ Everything in Essential Plan',
        '✅ Writing Studio with Expert Feedback (Selective)',
        '✅ 140+ Topic Tests (3 per topic)',
        '✅ Full OC & Selective Test Libraries',
        '✅ Exam-Style Interface Matching NSW Format',
        '✅ Step-by-Step Explanations for Every Question',
        '✅ Performance Comparison with Other Students',
        '✅ Advanced Analytics & Insights',
        '✅ Priority Support & Guidance',
        '✅ 7-Day Money-Back Guarantee',
        '✅ Regular Content Updates'
      ],
      cta: 'Get Premium Access',
      ctaAction: 'checkout-premium',
      popular: true,
      tier: 'premium'
    }
  ];

  return (
    <>
      <style>{`
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin-bottom: 60px;
        }
        
        @media (max-width: 1024px) {
          .pricing-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        }
      `}</style>
      
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <h1 style={{ 
          fontSize: '48px', 
          marginBottom: '20px',
          color: '#1f2937',
          fontWeight: 'bold'
        }}>
          Simple, Transparent Pricing
        </h1>
        <p style={{ fontSize: '20px', color: '#374151', maxWidth: '700px', margin: '0 auto', fontWeight: '500' }}>
          Start with our comprehensive free plan. Upgrade when you're ready for advanced features and more practice tests.
        </p>
      </div>

      {/* Pricing Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '30px',
        marginBottom: '60px'
      }}
      className="pricing-grid"
      >
        {plans.map((plan, index) => (
          <div 
            key={index}
            className="card"
            style={{ 
              padding: '0',
              position: 'relative',
              border: plan.popular ? '3px solid #f5576c' : '1px solid #e5e7eb',
              transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s ease'
            }}
          >
            {/* Popular Badge */}
            {plan.popular && (
              <div style={{
                position: 'absolute',
                top: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
              }}>
                ⭐ BEST VALUE
              </div>
            )}
            
            {/* Early Bird Badge */}
            {plan.earlyBird && (
              <div style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#10b981',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}>
                🎉 EARLY BIRD
              </div>
            )}

            {/* Header */}
            <div style={{
              background: plan.gradient,
              color: 'white',
              padding: '30px 20px',
              textAlign: 'center',
              borderRadius: '12px 12px 0 0'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>{plan.emoji}</div>
              <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>{plan.name}</h2>
              {plan.originalPrice && (
                <div style={{ 
                  fontSize: '20px', 
                  textDecoration: 'line-through', 
                  opacity: '0.7',
                  marginBottom: '5px'
                }}>
                  {plan.originalPrice}
                </div>
              )}
              <div style={{ fontSize: '40px', fontWeight: 'bold', marginBottom: '5px' }}>
                {plan.price}
              </div>
              <div style={{ fontSize: '14px', opacity: '0.9' }}>{plan.period}</div>
              {plan.earlyBird && (
                <div style={{ 
                  fontSize: '14px', 
                  marginTop: '10px',
                  padding: '8px 16px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  display: 'inline-block'
                }}>
                  Save $25 - Limited Time!
                </div>
              )}
            </div>

            {/* Features */}
            <div style={{ padding: '30px 20px' }}>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                fontSize: '14px',
                lineHeight: '1.8'
              }}>
                {plan.features.map((feature, i) => (
                  <li key={i} style={{ marginBottom: '10px', color: '#374151' }}>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {(() => {
                const isEssentialBtn = plan.ctaAction === 'checkout-essential';
                const isPremiumBtn = plan.ctaAction === 'checkout-premium';
                const isFreeBtn = typeof plan.ctaAction === 'function';
                
                // Determine button state
                let btnLabel = plan.cta;
                let btnDisabled = false;
                let btnBackground = plan.gradient;
                let btnOpacity = 1;
                
                if (isEssentialBtn) {
                  if (userTier === 'essential') {
                    btnLabel = '✓ Already Essential';
                    btnDisabled = true;
                    btnBackground = '#9ca3af';
                    btnOpacity = 0.7;
                  } else if (userTier === 'premium') {
                    btnLabel = '✓ Already Premium';
                    btnDisabled = true;
                    btnBackground = '#9ca3af';
                    btnOpacity = 0.7;
                  } else if (checkoutLoading) {
                    btnLabel = 'Loading...';
                    btnDisabled = true;
                  }
                } else if (isPremiumBtn) {
                  if (userTier === 'premium') {
                    btnLabel = '✓ Already Premium';
                    btnDisabled = true;
                    btnBackground = '#9ca3af';
                    btnOpacity = 0.7;
                  } else if (userTier === 'essential') {
                    btnLabel = '🚀 Upgrade to Premium';
                    btnDisabled = false;
                  } else if (checkoutLoading) {
                    btnLabel = 'Loading...';
                    btnDisabled = true;
                  }
                }
                
                return (
                  <button
                    onClick={() => {
                      if (btnDisabled) return;
                      if (isEssentialBtn) handleCheckout('essential');
                      else if (isPremiumBtn) handleCheckout('premium');
                      else if (isFreeBtn) plan.ctaAction();
                    }}
                    disabled={btnDisabled}
                    style={{
                      width: '100%',
                      padding: '14px',
                      marginTop: '20px',
                      background: btnBackground,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: btnDisabled ? 'not-allowed' : 'pointer',
                      transition: 'transform 0.2s ease',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      opacity: btnOpacity
                    }}
                    onMouseEnter={(e) => !btnDisabled && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    {btnLabel}
                  </button>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {/* Why Choose PrepSmart */}
      <div className="card" style={{ padding: '50px', background: '#f9fafb', marginBottom: '60px' }}>
        <h2 style={{ 
          fontSize: '36px', 
          textAlign: 'center', 
          marginBottom: '40px',
          color: '#4f46e5'
        }}>
          Why Choose PrepSmart?
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', 
          gap: '30px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>💰</div>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Affordable Excellence
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Quality test preparation at a fraction of tutoring costs. Starting from $75 vs $500+ for tutoring.
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>💡</div>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Smart Feedback System
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Get instant, personalized feedback on every question. Learn from mistakes with expert explanations.
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📊</div>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Track Progress
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Monitor improvement over time. Identify weak areas and focus your practice effectively.
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>🎯</div>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Exam-Focused
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              All tests follow NSW Selective & OC Placement Test format and standards.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="card" style={{ padding: '50px' }}>
        <h2 style={{ 
          fontSize: '36px', 
          textAlign: 'center', 
          marginBottom: '40px',
          color: '#4f46e5'
        }}>
          Frequently Asked Questions
        </h2>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Is the free plan really free forever?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Yes! Our free plan includes practice tests for both Selective and OC programs with unlimited attempts. No credit card required, no hidden fees.
            </p>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Does the same plan cover both OC and Selective?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Yes! All plans include content for both the OC Placement Test and the Selective High School Placement Test. Your child sees content relevant to their chosen program.
            </p>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              What's the difference between Mock Tests and Topic-Based Tests?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Mock Tests are full-length practice exams covering all topics. Topic-Based Tests focus on specific skills (e.g., Algebra, Comprehension, Logic Puzzles) for targeted practice.
            </p>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Is this a monthly subscription?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              No! It's a one-time payment with access until the May exam. Essential is $75 AUD and Premium is $100 AUD. No recurring charges.
            </p>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              What if I'm not satisfied with the Premium Bundle?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              We offer a 7-day money-back guarantee. If you're not happy with your purchase, contact us for a full refund.
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '20px', marginBottom: '10px', color: '#1f2937' }}>
              Can I share my account with siblings?
            </h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Each student needs their own account to track individual progress. Contact us for family discounts!
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        color: 'white',
        marginTop: '60px'
      }}>
        <h2 style={{ fontSize: '36px', marginBottom: '20px' }}>
          Ready to Start Practicing?
        </h2>
        <p style={{ fontSize: '20px', marginBottom: '30px', opacity: '0.9' }}>
          Join students preparing for NSW Selective & OC placement exams
        </p>
        <button
          onClick={() => navigate('/register')}
          style={{
            padding: '16px 48px',
            background: 'white',
            color: '#667eea',
            border: 'none',
            borderRadius: '8px',
            fontSize: '20px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          Get Started Free →
        </button>
      </div>
    </div>
    </>
  );
}

export default Pricing;
